import * as React from "react";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "../ui/tooltip";
import { ICompasAIChatAction } from "@/types/compass.types";
import { getActionCardClass, ActionItemEditForm } from "./action-item.utils";
import { StatusIcon } from "./StatusIcon";
import { ActionItemContentDisplay } from "./ActionItemContentDisplay";
import { ActionItemActions } from "./ActionItemActions";

interface ActionItemCardBodyProps {
  action: ICompasAIChatAction;
  isExpanded: boolean;
  toggleExpansion: () => void;
  isEditing: boolean;
  editedPrompt: string;
  setEditedPrompt: (v: string) => void;
  handleCancelEditing: () => void;
  onToggleEditing: () => void;
  handleDeleteAction: () => void;
  handleSaveToLibrary: () => void;
  isPending: boolean;
  currentStatus: string;
  handleCardClick: () => void;
}

const ActionItemTooltipContent: React.FC<{ currentStatus: string }> = ({ currentStatus }) => (
  <p>
    {currentStatus === "queued" || currentStatus === "pending"
      ? "Click to cancel this queued action"
      : currentStatus === "processed" || currentStatus === "completed"
        ? "Click to re-run the action"
        : currentStatus === "failed"
          ? 'Click "Edit" to modify and re-run, or delete the action'
          : 'Click to initiate the action or click "Edit" button to modify'}
  </p>
);

export const ActionItemCardBody: React.FC<ActionItemCardBodyProps> = ({
  action,
  isExpanded,
  toggleExpansion,
  isEditing,
  editedPrompt,
  setEditedPrompt,
  handleCancelEditing,
  onToggleEditing,
  handleDeleteAction,
  handleSaveToLibrary,
  isPending,
  currentStatus,
  handleCardClick,
}) => (
  <TooltipProvider>
    <Tooltip>
      <TooltipTrigger asChild>
        <div
          className={`${getActionCardClass(currentStatus)} flex items-start gap-2`}
          onClick={handleCardClick}
        >
          <div className="grow flex flex-col gap-2">
            <div className="flex items-center gap-2">
              <StatusIcon currentStatus={currentStatus} />
              <h6 className="text-sm font-semibold">
                {action.heading || action.label || "Action Item"}
              </h6>
            </div>
            {isEditing ? (
              <ActionItemEditForm
                editedPrompt={editedPrompt}
                setEditedPrompt={setEditedPrompt}
                handleCancelEditing={handleCancelEditing}
                handleToggleEditing={onToggleEditing}
                isPending={isPending}
              />
            ) : (
              <ActionItemContentDisplay
                action={action}
                isExpanded={isExpanded}
                currentStatus={currentStatus}
              />
            )}
          </div>
          {!isEditing && (
            <div className="flex items-start gap-2">
              <ActionItemActions
                action={action}
                isExpanded={isExpanded}
                toggleExpansion={toggleExpansion}
                handleToggleEditing={onToggleEditing}
                handleDeleteAction={handleDeleteAction}
                handleSaveToLibrary={handleSaveToLibrary}
                currentStatus={currentStatus}
              />
            </div>
          )}
        </div>
      </TooltipTrigger>
      <TooltipContent className="max-w-[260px]">
        <ActionItemTooltipContent currentStatus={currentStatus} />
      </TooltipContent>
    </Tooltip>
  </TooltipProvider>
);
